import { useEffect, useMemo, useRef } from 'react';

import { useProject } from '@contexts/Project.context';
import { useCollectionStore } from '@stores/useCollectionStore';
import { useWebGLVectorLayer } from '@hooks/useWebGLVectorLayer';
import { useVectorLayer } from '@hooks/useVectorLayer';
import { DETECTION_POINTER_LAYER_ID, DETECTION_POINTER, DETECTION_POINTERS } from './constants';
import { buildShapeStyles, buildShapeVariables } from './utils/collectionMapStyle';
import { bucketFeaturesByShape, reconcileShapeSource } from './utils/collectionMapSource';
import { buildSegmentFeature, buildSegmentStyle, splitSegments } from './utils/collectionSegments';

/** Below every shape layer: a road's surface must not cover the cars on it. */
const SEGMENT_Z_INDEX = 101;
const SEGMENT_LAYER_ID = 'collection-segments';

/**
 * z-indices sit above imagery (10–99), within the analysis band (100+), ordered
 * by footprint with the largest at the bottom: the translucent bbox fill would
 * tint an opaque dot beneath it, and OpenLayers reports the topmost feature
 * first, so a 3px dot must win the hover test against a large box.
 */
const Z_INDEX = {
	[DETECTION_POINTER.BOUNDING_BOX]: 102,
	[DETECTION_POINTER.OUTLINE]: 103,
	[DETECTION_POINTER.CENTER_POINT]: 104,
};

const EMPTY_RENDERED = () => Object.fromEntries(DETECTION_POINTERS.map(s => [s, new Map()]));

/**
 * Renders collection detection features on the OL map, each object drawn as the
 * shape its detection pointer asks for — its center point, bounding box, or
 * outline.
 *
 * One WebGLVectorLayer per shape, all three live at once. Every feature is
 * bucketed to exactly one of them, so the total rendered feature count matches
 * the number of visible detections and changing an object's pointer simply moves
 * its features from one layer to another. Sources are updated by diffing against
 * the desired set (add/remove only the delta) rather than clearing and
 * rebuilding, so unchanged features never disappear and the map doesn't blink.
 *
 * Data flow:
 *   useCollectionStore.filteredFeatures + pointerByKey
 *     → bucketFeaturesByShape → reconcileShapeSource → each shape's source
 *   objects[] (which objects exist)      → buildShapeStyles    → WebGL style
 *   objects[].color/opacity (+ live edit) → buildShapeVariables → style variables
 *
 * @param {object} props
 * @param {Array}  props.objects - Collection objects (key, color, opacity).
 * @param {{ objectKey, color?, opacity? }|null} props.liveStyling - live style
 *   override for the object whose styling is being edited; null otherwise. It
 *   can change in bursts (a hex field previews per keystroke), so it must only
 *   ever reach the layers as style variables.
 * @param {Object} props.pointerByKey - object key (node uuid) → effective detection pointer.
 *   Identity-stable (see useDetectionPointer): it gates the reconcile effect, and
 *   a fresh object on every refetch would re-diff the whole set each render.
 */
const CollectionMapLayer = ({ objects, liveStyling = null, pointerByKey }) => {
	const { mapObject, selectedSingleImage } = useProject();
	const filteredFeatures = useCollectionStore(s => s.filteredFeatures);

	// One layer per shape. useWebGLVectorLayer registers on the map and cleans up
	// on unmount. Initial style is empty — set after creation.
	const centerLayer = useWebGLVectorLayer(
		mapObject,
		DETECTION_POINTER_LAYER_ID[DETECTION_POINTER.CENTER_POINT],
		{
			name: 'Center points',
			zIndex: Z_INDEX[DETECTION_POINTER.CENTER_POINT],
			visible: true,
		}
	);
	const outlineLayer = useWebGLVectorLayer(
		mapObject,
		DETECTION_POINTER_LAYER_ID[DETECTION_POINTER.OUTLINE],
		{
			name: 'Outline',
			zIndex: Z_INDEX[DETECTION_POINTER.OUTLINE],
			visible: true,
		}
	);
	const bboxLayer = useWebGLVectorLayer(
		mapObject,
		DETECTION_POINTER_LAYER_ID[DETECTION_POINTER.BOUNDING_BOX],
		{
			name: 'Bounding box',
			zIndex: Z_INDEX[DETECTION_POINTER.BOUNDING_BOX],
			visible: true,
		}
	);

	// Roads, rivers and other continuous things saved by Find: lines and surfaces, drawn on a
	// canvas layer of their own (see collectionSegments).
	const segmentLayer = useVectorLayer(mapObject, SEGMENT_LAYER_ID, {
		name: 'Roads and areas',
		zIndex: SEGMENT_Z_INDEX,
		visible: true,
	});

	const layerByShape = useMemo(
		() => ({
			[DETECTION_POINTER.CENTER_POINT]: centerLayer,
			[DETECTION_POINTER.OUTLINE]: outlineLayer,
			[DETECTION_POINTER.BOUNDING_BOX]: bboxLayer,
		}),
		[centerLayer, outlineLayer, bboxLayer]
	);

	// Keyed on the object set, not on the `objects` array identity: every config
	// write hands back a fresh array, and `setStyle` disposes the WebGL renderer —
	// a shader recompile plus buffer regeneration for every feature in all three
	// layers. Nothing but adding, removing or reordering an object should pay
	// that: a pointer pick changes no color, and a color change moves a uniform.
	const styleKey = (objects ?? []).map(o => o.key).join('|');
	const styles = useMemo(
		() => buildShapeStyles(objects),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- styleKey stands in for `objects`
		[styleKey]
	);

	// The colors the style left as variables. Keyed on the colors themselves for
	// the same reason: a refetch that changed nothing must not push uniforms.
	const colorKey = (objects ?? []).map(o => `${o.key}:${o.color}:${o.opacity}`).join('|');
	const variables = useMemo(
		() => buildShapeVariables(objects, liveStyling),
		// eslint-disable-next-line react-hooks/exhaustive-deps -- colorKey stands in for `objects`
		[colorKey, liveStyling]
	);

	// Declared *before* the setStyle effect on purpose. Effects run in declaration
	// order, so on the one commit where both change — an object added or removed —
	// the uniforms are in place before the renderer is rebuilt, and no frame is
	// drawn against unset variables. A color edit runs only this one, which is the
	// whole point: uniforms and a re-render, no recompile.
	useEffect(() => {
		for (const [shape, layer] of Object.entries(layerByShape)) {
			layer?.updateStyleVariables(variables[shape]);
		}
	}, [layerByShape, variables]);

	useEffect(() => {
		for (const [shape, layer] of Object.entries(layerByShape)) layer?.setStyle(styles[shape]);
	}, [layerByShape, styles]);

	useEffect(() => {
		segmentLayer?.setStyle(buildSegmentStyle(objects, liveStyling));
		// eslint-disable-next-line react-hooks/exhaustive-deps -- colorKey stands in for `objects`
	}, [segmentLayer, colorKey, liveStyling]);

	const { detections, segments } = useMemo(
		() => splitSegments(filteredFeatures),
		[filteredFeatures]
	);

	// Per-shape map of GeoJSON feature → built OL Feature, so each source can be
	// diffed instead of cleared+rebuilt.
	const renderedRef = useRef(null);
	if (renderedRef.current === null) renderedRef.current = EMPTY_RENDERED();

	// The source each shape's rendered map was built against. A new map object
	// makes useWebGLVectorLayer rebuild the layers with fresh, empty sources; the
	// rendered map would then claim every feature is already drawn and the diff
	// would add nothing, leaving the recreated map blank.
	const sourceRef = useRef({});

	// Reconcile every shape's source against its bucket. All three must happen in
	// this one synchronous pass: a feature whose pointer changed leaves one bucket
	// and joins another, and reconciling the layers in separate effects would give
	// it a frame in both sources (drawn twice) or in neither (a blink).
	useEffect(() => {
		if (Object.values(layerByShape).some(layer => !layer)) return;

		const buckets = bucketFeaturesByShape(detections, pointerByKey);
		for (const [shape, layer] of Object.entries(layerByShape)) {
			const source = layer.getSource();

			// Reconciling against a source this shape has never been drawn into
			// starts from nothing rendered, whatever the previous source held.
			if (sourceRef.current[shape] !== source) {
				renderedRef.current[shape] = new Map();
				sourceRef.current[shape] = source;
			}

			reconcileShapeSource(source, renderedRef.current[shape], buckets[shape], shape);
		}
	}, [layerByShape, pointerByKey, detections]);

	// The segment layer holds few features; rebuilding it on a filter change is cheap.
	useEffect(() => {
		if (!segmentLayer) return;
		const source = segmentLayer.getSource();
		source.clear(true);
		source.addFeatures(segments.map(buildSegmentFeature).filter(Boolean));
	}, [segmentLayer, segments]);

	// Hide every shape while a single image is open, so these approximate
	// map-position shapes don't render alongside CollectionSingleImageLayer's
	// precise per-image overlay (always a no-op in orthophoto mode, since
	// selectedSingleImage is always null there). The reconciliation effect above
	// still runs while hidden, so the sources stay in sync and reopening the map
	// view doesn't need to catch up.
	useEffect(() => {
		for (const layer of [...Object.values(layerByShape), segmentLayer]) {
			layer?.setVisible(!selectedSingleImage);
		}
	}, [layerByShape, segmentLayer, selectedSingleImage]);

	return null;
};

export default CollectionMapLayer;
